import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';

// ─── 🧭 Event Delivery Map — the event's sales cycle as one timeline ────────────
// The strategic compass for an event: launch, ticket drops, lineup reveals,
// marketing pushes and Pulse's own scheduled campaigns, ordered over time. One
// component serves BOTH surfaces (CLAUDE.md dual-surface rule) via `scope`:
//   scope="my"    → the client, on their home dashboard (self-service entry)
//   scope="admin" → Howler staff, from the client's "Delivery map" tab (AM entry)
// Talks to server/deliveryMap.js; who may WRITE is decided there (goals.manage),
// surfaced as `canManage` — the editor affordances simply don't render without it.
//
// MOBILE-FIRST: the vertical, month-grouped rail IS the design; on desktop we add
// a horizontal track above it (the "map" view). Nothing is side-by-side on a
// phone, every control is a ≥40px tap target, and no width is fixed.
export default function DeliveryMap({ entityId, suites = [], scope = 'my', dismissible = false }) {
  const isMobile = useIsMobile();
  const list = (suites || []).filter(Boolean);
  const listKey = list.map((s) => s.id).join(','); // callers rebuild the array each render
  const [suiteId, setSuiteId] = useState('');
  const [data, setData] = useState(null);      // null = loading, {} on failure
  const [editing, setEditing] = useState(null); // milestone being edited, or 'new'
  const [busyId, setBusyId] = useState('');
  const [err, setErr] = useState('');

  // Which event's map are we looking at? Remembered per client so a return visit
  // opens where they left off; falls back to the first (soonest) event.
  const pickKey = `howler_delivery_map_suite:${entityId || 'x'}`;
  useEffect(() => {
    if (!list.length) { setSuiteId(''); return; }
    let saved = '';
    try { saved = localStorage.getItem(pickKey) || ''; } catch { /* private mode */ }
    setSuiteId(list.some((s) => s.id === saved) ? saved : list[0].id);
  }, [listKey, pickKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickSuite = (id) => { setSuiteId(id); try { localStorage.setItem(pickKey, id); } catch { /* ignore */ } };

  // Hidden per client on home (like the goals strip) — remembered locally.
  const hideKey = `howler_delivery_map_hidden:${entityId || 'x'}`;
  const [hidden, setHidden] = useState(false);
  useEffect(() => { if (dismissible) { try { setHidden(!!localStorage.getItem(hideKey)); } catch { setHidden(false); } } }, [hideKey, dismissible]);

  // The client's own rows plus the read-only campaign pins, in one ordered line.
  // (Computed before any early return — hooks must not sit behind a condition.)
  const rows = useMemo(() => [...(data?.milestones || []), ...(data?.derived || [])]
    .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || '')), [data]);

  // A request token drops stale responses (fast event-chip switching), and a
  // failed load says so instead of masquerading as an empty map.
  const reqRef = useRef(0);
  const load = useCallback((sid) => {
    if (!sid) return;
    const req = ++reqRef.current;
    api.deliveryMap(scope, sid)
      .then((d) => { if (req === reqRef.current) setData(d); })
      .catch(() => { if (req === reqRef.current) setData({ failed: true }); });
  }, [scope]);
  useEffect(() => { setData(null); setErr(''); load(suiteId); }, [suiteId, load]);

  const save = async (body) => {
    setErr('');
    try {
      if (editing && editing !== 'new') await api.updateMilestone(scope, suiteId, editing.id, body);
      else await api.createMilestone(scope, suiteId, body);
      setEditing(null);
      load(suiteId);
    } catch (e) { setErr(e.message || 'Could not save that milestone.'); throw e; }
  };
  const remove = async (m) => {
    setBusyId(m.id); setErr('');
    try { await api.deleteMilestone(scope, suiteId, m.id); load(suiteId); }
    catch (e) { setErr(e.message || 'Could not remove that milestone.'); }
    finally { setBusyId(''); }
  };

  if (!list.length || hidden) return null;

  const suite = list.find((s) => s.id === suiteId);
  const kinds = data?.kinds || [];
  const canManage = !!data?.canManage;
  // On home, an empty map with no way to fill it is just noise on someone's
  // dashboard — stay invisible for read-only teammates until there's a map.
  if (dismissible && data && !rows.length && !canManage) return null;
  const anchors = data?.anchors || {};
  const today = isoToday();
  const win = timeWindow(rows, anchors, today);
  const next = rows.find((r) => (r.startDate || '') >= today);

  return (
    <section style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 7 }}>
          <span>🧭</span>Event delivery map
        </h2>
        {next && <span style={pill} title="The next moment on the map">Next: {shortDate(next.startDate)} · {next.label}</span>}
        <span style={{ flex: 1 }} />
        {canManage && <button onClick={() => setEditing('new')} style={addBtn}>＋ Add milestone</button>}
        {dismissible && (
          <button onClick={() => { try { localStorage.setItem(hideKey, '1'); } catch { /* ignore */ } setHidden(true); }}
            title="Hide the delivery map from your home" aria-label="Hide the delivery map" style={dismissBtn}>✕</button>
        )}
      </div>

      {/* Which event (only worth showing when they have more than one) */}
      {list.length > 1 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}>
          {list.map((s) => (
            <button key={s.id} onClick={() => pickSuite(s.id)} style={eventChip(s.id === suiteId)}>{s.icon ? `${s.icon} ` : ''}{s.name}</button>
          ))}
        </div>
      )}

      <div style={card}>
        {data == null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div className="skel" style={{ width: '100%', height: 34 }} />
            <div className="skel" style={{ width: '72%', height: 13 }} />
            <div className="skel" style={{ width: '84%', height: 13 }} />
          </div>
        ) : data.failed ? (
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
            Couldn&apos;t load the delivery map.{' '}
            <button onClick={() => { setData(null); load(suiteId); }} style={{ border: 'none', background: 'none', color: 'var(--brand, #6d5df6)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>Try again</button>
          </p>
        ) : !rows.length ? (
          <EmptyState canManage={canManage} suiteName={suite?.name} onAdd={() => setEditing('new')} />
        ) : (
          <>
            {/* Desktop only: the horizontal "map". The rail carries the on-sale
                window; each moment is a kind-coloured dot at its date. On a phone
                this is dropped entirely — the rail below is the mobile design. */}
            {!isMobile && <Track rows={rows} win={win} anchors={anchors} today={today} kinds={kinds} />}
            <Rail rows={rows} today={today} kinds={kinds} canManage={canManage} busyId={busyId}
              onEdit={setEditing} onRemove={remove} />
          </>
        )}
        {err && <p style={{ color: 'var(--error)', fontSize: 12.5, marginTop: 10 }}>{err}</p>}
      </div>

      {editing && (
        <MilestoneEditor
          milestone={editing === 'new' ? null : editing}
          kinds={kinds.length ? kinds : FALLBACK_KINDS}
          anchors={anchors}
          onClose={() => { setEditing(null); setErr(''); }}
          onSave={save}
        />
      )}
    </section>
  );
}

// ── The horizontal map (desktop) ───────────────────────────────────────────────
// Deliberately label-free: dots + a hover title, with the reading done in the
// rail below. Rotated or stacked labels collide the moment two moments land in
// the same week, and a colliding label is worse than no label.
function Track({ rows, win, anchors, today, kinds }) {
  const pct = (d) => (win.span > 0 ? Math.max(0, Math.min(100, ((ms(d) - win.from) / win.span) * 100)) : 50);
  const onSale = anchors.launchDate && (anchors.eventEnd || anchors.eventStart)
    ? { left: pct(anchors.launchDate), right: pct(anchors.eventEnd || anchors.eventStart) } : null;
  const label = (k) => (kinds.find((x) => x.key === k) || {}).label || k;
  return (
    <div style={{ padding: '6px 4px 16px' }}>
      <div style={{ position: 'relative', height: 46 }}>
        {/* the rail */}
        <div style={{ position: 'absolute', left: 0, right: 0, top: 20, height: 6, borderRadius: 999, background: 'rgba(128,128,128,0.18)' }} />
        {onSale && (
          <div title="On sale" style={{ position: 'absolute', top: 20, height: 6, borderRadius: 999, background: 'rgba(var(--brand-rgb),0.35)', left: `${onSale.left}%`, width: `${Math.max(1, onSale.right - onSale.left)}%` }} />
        )}
        {/* today */}
        {today >= win.fromIso && today <= win.toIso && (
          <div style={{ position: 'absolute', top: 4, bottom: 4, left: `${pct(today)}%`, width: 2, background: 'var(--text)', opacity: 0.35 }} title={`Today · ${shortDate(today)}`} />
        )}
        {/* the moments */}
        {rows.map((m) => (
          <span key={m.id} title={`${shortDate(m.startDate)} · ${label(m.kind)} · ${m.label}`}
            style={{
              position: 'absolute', top: 15, left: `${pct(m.startDate)}%`, transform: 'translateX(-50%)',
              width: 16, height: 16, borderRadius: 999, background: kindColor(m.kind),
              border: '2px solid var(--card)', boxSizing: 'border-box',
              opacity: m.status === 'done' ? 0.5 : 1,
            }} />
        ))}
      </div>
      {/* the axis: first month, today, last month — three honest labels, no clutter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)' }}>
        <span>{shortDate(win.fromIso)}</span>
        {anchors.eventStart && <span title="Event day">🎉 {shortDate(anchors.eventStart)}</span>}
        <span>{shortDate(win.toIso)}</span>
      </div>
    </div>
  );
}

// ── The vertical rail (mobile + desktop) — the readable timeline ───────────────
function Rail({ rows, today, kinds, canManage, busyId, onEdit, onRemove }) {
  const label = (k) => (kinds.find((x) => x.key === k) || {}).label || k;
  const emoji = (k) => (kinds.find((x) => x.key === k) || {}).emoji || '📌';
  let lastMonth = '';
  let todayDrawn = false;
  const out = [];
  for (const m of rows) {
    const month = (m.startDate || '').slice(0, 7);
    if (month !== lastMonth) { out.push(<div key={`m-${month}`} style={monthHead}>{monthLabel(m.startDate)}</div>); lastMonth = month; }
    if (!todayDrawn && (m.startDate || '') >= today) {
      out.push(<div key="today" style={todayRow}><span style={todayDot} />Today · {shortDate(today)}</div>);
      todayDrawn = true;
    }
    const past = (m.startDate || '') < today;
    out.push(
      <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0' }}>
        {/* the rail dot + kind colour */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 22, paddingTop: 3 }}>
          <span style={{ width: 12, height: 12, borderRadius: 999, background: kindColor(m.kind), opacity: m.status === 'done' || past ? 0.55 : 1 }} />
          <span style={{ flex: 1, width: 2, minHeight: 12, background: 'rgba(128,128,128,0.22)', marginTop: 4 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
              {shortDate(m.startDate)}{m.endDate ? ` → ${shortDate(m.endDate)}` : ''}
            </span>
            {/* Identity is emoji + words, never colour alone. */}
            <span style={kindChip}>{emoji(m.kind)} {label(m.kind)}</span>
            {m.status === 'done' && <span style={doneChip}>✓ Done</span>}
            {m.source === 'campaign' && <span style={fromChip} title="A campaign scheduled in Engage — edit it there">from Engage</span>}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, marginTop: 3, textDecoration: m.status === 'done' ? 'line-through' : 'none', opacity: m.status === 'done' ? 0.7 : 1 }}>{m.label}</div>
          {m.notes && <div style={{ fontSize: 12.5, color: 'var(--muted-2)', lineHeight: 1.5, marginTop: 3, whiteSpace: 'pre-wrap' }}>{m.notes}</div>}
          {canManage && !m.locked && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={() => onEdit(m)} style={rowBtn}>Edit</button>
              <button onClick={() => onRemove(m)} disabled={busyId === m.id} style={rowBtn}>{busyId === m.id ? '…' : 'Remove'}</button>
            </div>
          )}
        </div>
      </div>,
    );
  }
  // Everything is in the past — say so rather than leaving the rail unmarked.
  if (!todayDrawn) out.push(<div key="today" style={todayRow}><span style={todayDot} />Today · {shortDate(today)}</div>);
  return <div>{out}</div>;
}

function EmptyState({ canManage, suiteName, onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '18px 6px' }}>
      <div style={{ fontSize: 26 }}>🧭</div>
      <p style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>Start building {suiteName ? `${suiteName}'s` : 'your'} delivery map</p>
      <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, maxWidth: 420, margin: '6px auto 0' }}>
        Plot the moments that shape the sales cycle — launch, ticket drops, lineup reveals, marketing pushes, production dates.
        They line up on one timeline so you can plan the next push and review what worked.
      </p>
      {canManage
        ? <button onClick={onAdd} style={{ ...addBtn, marginTop: 14 }}>＋ Add your first milestone</button>
        : <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12 }}>Ask your Howler account manager (or a teammate who manages goals) to plot the first moments.</p>}
    </div>
  );
}

// ── Add / edit one milestone ───────────────────────────────────────────────────
function MilestoneEditor({ milestone, kinds, anchors, onClose, onSave }) {
  const isMobile = useIsMobile();
  const [kind, setKind] = useState(milestone?.kind || 'launch');
  const [label, setLabel] = useState(milestone?.label || '');
  const [startDate, setStartDate] = useState(milestone?.startDate || anchors?.launchDate || isoToday());
  const [endDate, setEndDate] = useState(milestone?.endDate || '');
  const [status, setStatus] = useState(milestone?.status || 'planned');
  const [notes, setNotes] = useState(milestone?.notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!label.trim()) { setErr('Give the milestone a name.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) { setErr('Pick a date.'); return; }
    setBusy(true); setErr('');
    try { await onSave({ kind, label: label.trim(), startDate, endDate, status, notes }); }
    catch (e) { setErr(e.message || 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={{ ...modal, width: isMobile ? '100%' : 460 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', flex: 1 }}>
            🧭 {milestone ? 'Edit milestone' : 'Add milestone'}
          </div>
          <button onClick={onClose} style={xBtn} aria-label="Close">✕</button>
        </div>

        <Label>What kind of moment?</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {kinds.map((k) => (
            <button key={k.key} onClick={() => setKind(k.key)} style={kindPick(k.key === kind, kindColor(k.key))}>{k.emoji} {k.label}</button>
          ))}
        </div>

        <Label>Name it</Label>
        <input style={input} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120}
          placeholder="e.g. Phase 2 tickets on sale" autoFocus />

        {/* Stacked on mobile — never a squished side-by-side pair on a phone. */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0 : 10 }}>
          <div style={{ flex: 1 }}>
            <Label>Date</Label>
            <input style={input} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Runs until <span style={opt}>· optional</span></Label>
            <input style={input} type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <Label>Status</Label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['planned', 'Planned'], ['done', '✓ Done']].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} style={kindPick(status === k, 'var(--brand)')}>{l}</button>
          ))}
        </div>

        <Label>Notes <span style={opt}>· optional</span></Label>
        <textarea style={{ ...input, minHeight: 74, resize: 'vertical' }} value={notes} maxLength={600}
          onChange={(e) => setNotes(e.target.value)} placeholder="What's the plan, who owns it, what it depends on…" />

        {err && <p style={{ color: 'var(--error)', fontSize: 13, marginTop: 12 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button style={ghost} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={primary} onClick={submit} disabled={busy}>{busy ? 'Saving…' : milestone ? 'Save' : 'Add to map'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Dates + colour ────────────────────────────────────────────────────────────
const isoToday = () => new Date().toISOString().slice(0, 10);
const ms = (iso) => Date.parse(`${iso}T00:00:00Z`);
const shortDate = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '');
const monthLabel = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '');

// The span the track draws: every moment + the event's anchors + today, padded so
// the first and last dots aren't welded to the edges.
function timeWindow(rows, anchors, today) {
  const dates = [...rows.map((r) => r.startDate), ...rows.map((r) => r.endDate), anchors.launchDate, anchors.eventStart, anchors.eventEnd, today]
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || '')));
  if (!dates.length) return { from: ms(today), to: ms(today), span: 0, fromIso: today, toIso: today };
  const sorted = [...dates].sort();
  const pad = 3 * 86400000;
  const from = ms(sorted[0]) - pad;
  const to = ms(sorted[sorted.length - 1]) + pad;
  return { from, to, span: to - from, fromIso: new Date(from).toISOString().slice(0, 10), toIso: new Date(to).toISOString().slice(0, 10) };
}

// Milestone kinds → a fixed, well-separated hue each (assigned in order, never
// cycled). Colour is ALWAYS paired with the kind's emoji + name in the UI, so a
// colourblind or greyscale reader loses nothing. Keys mirror server/deliveryMap.js.
const KIND_COLOR = {
  launch: '#ff385c',     // brand rose — the on-sale moment
  tickets: '#0ea5e9',    // sky
  lineup: '#8b5cf6',     // violet
  marketing: '#f59e0b',  // amber
  campaign: '#10b981',   // emerald
  ops: '#0f766e',        // deep teal
  other: '#64748b',      // slate
};
const kindColor = (k) => KIND_COLOR[k] || KIND_COLOR.other;
// Only used if a (very old) cached response arrives without the catalogue.
const FALLBACK_KINDS = [
  { key: 'launch', emoji: '🚀', label: 'Launch / on sale' },
  { key: 'tickets', emoji: '🎟️', label: 'Ticket drop' },
  { key: 'lineup', emoji: '🎤', label: 'Lineup release' },
  { key: 'marketing', emoji: '📣', label: 'Marketing push' },
  { key: 'campaign', emoji: '✉️', label: 'Campaign' },
  { key: 'ops', emoji: '🛠', label: 'Production / ops' },
  { key: 'other', emoji: '📌', label: 'Other moment' },
];

// ── Styles (mobile-first: no fixed widths, ≥40px tap targets on the controls) ──
const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 16, padding: 14 };
const pill = { fontSize: 11.5, fontWeight: 700, color: 'var(--brand)', background: 'rgba(var(--brand-rgb),0.10)', borderRadius: 980, padding: '4px 10px' };
const addBtn = { minHeight: 40, padding: '9px 14px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 980, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const dismissBtn = { border: 'none', background: 'rgba(128,128,128,0.12)', color: 'var(--muted-2)', borderRadius: 980, width: 40, height: 40, fontSize: 12, cursor: 'pointer', flexShrink: 0 };
const eventChip = (on) => ({ minHeight: 40, padding: '8px 13px', borderRadius: 980, border: `1px solid ${on ? 'var(--brand)' : 'var(--hairline)'}`, background: on ? 'rgba(var(--brand-rgb),0.10)' : 'transparent', color: on ? 'var(--brand)' : 'var(--muted-2)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit' });
const monthHead = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', margin: '10px 0 2px' };
const todayRow = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 700, color: 'var(--brand)', padding: '6px 0' };
const todayDot = { width: 8, height: 8, borderRadius: 999, background: 'var(--brand)', marginLeft: 7 };
const kindChip = { fontSize: 11, fontWeight: 700, color: 'var(--muted-2)', background: 'rgba(128,128,128,0.12)', borderRadius: 980, padding: '2px 8px' };
const doneChip = { fontSize: 11, fontWeight: 700, color: '#0f766e', background: 'rgba(15,118,110,0.12)', borderRadius: 980, padding: '2px 8px' };
const fromChip = { fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.12)', borderRadius: 980, padding: '2px 8px' };
const rowBtn = { minHeight: 40, padding: '8px 12px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--muted-2)', borderRadius: 980, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const kindPick = (on, colour) => ({ minHeight: 40, padding: '8px 12px', borderRadius: 980, border: `1px solid ${on ? colour : 'var(--hairline)'}`, background: on ? `${colour}1f` : 'transparent', color: on ? 'var(--text)' : 'var(--muted-2)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' });
const Label = ({ children }) => <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', margin: '12px 0 4px' }}>{children}</label>;
const opt = { textTransform: 'none', letterSpacing: 0, fontWeight: 400 };
const backdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modal = { background: 'var(--card)', borderRadius: 16, padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', maxHeight: '90dvh', overflowY: 'auto', boxSizing: 'border-box' };
const input = { width: '100%', padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--card)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit' };
const primary = { minHeight: 40, padding: '9px 18px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 980, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghost = { minHeight: 40, padding: '9px 16px', background: 'transparent', color: 'var(--text)', border: '1px solid var(--hairline)', borderRadius: 980, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const xBtn = { background: 'none', border: 'none', fontSize: 16, color: 'var(--muted)', cursor: 'pointer', lineHeight: 1, minHeight: 40, minWidth: 40 };
